import { ApiBase } from '../api-base';
import { inject } from 'aurelia';
import { IHttpClient } from '@aurelia/fetch-client';
import { EventEntity } from './event-entity';
import { EventCommentEntity } from './event-comment-entity';

@inject(IHttpClient)
export class EventApi extends ApiBase {
    private _baseUrl: string = 'api/events';

    constructor(http: IHttpClient) {
        super(http);
    }

    public async add(entity: EventEntity): Promise<EventEntity> {
        const dto = entity.toDto();
        const response = await this.post(this._baseUrl, dto);
        return EventEntity.fromDto(response.content);
    }

    public async update(entity: EventEntity): Promise<EventEntity> {
        const dto = entity.toDto();
        const response = await this.patch(this._baseUrl + '/' + entity.id, dto);
        return EventEntity.fromDto(response.content);
    }

    public async getById(id: string): Promise<EventEntity> {
        const response = await this.get(this._baseUrl + '/' + id);
        return EventEntity.fromDto(response.content);
    }

    public async getByToken(code: string, token: string): Promise<EventEntity> {
        const response = await this.get(this._baseUrl + '/bytoken', { code, token });
        return EventEntity.fromDto(response.content);
    }

    public async getByPin(code: string, pin: string): Promise<EventEntity> {
        const dto = {
            code,
            pin
        };
        const response = await this.post(this._baseUrl + '/bypin', dto);
        return EventEntity.fromDto(response.content);
    }

    public async getMine(): Promise<{ events: EventEntity[]; invitesCount: number }> {
        const response = await this.get(this._baseUrl + '/mine');
        const content = response.content as { events: any[]; invitesCount: number };
        return {
            events: EventEntity.fromDtoArray(content?.events ?? []),
            invitesCount: content?.invitesCount ?? 0,
        };
    }

    public async getInvites(): Promise<EventEntity[]> {
        const response = await this.get(this._baseUrl + '/invites');
        return EventEntity.fromDtoArray(response.content);
    }

    public async orphan(id: string): Promise<void> {
        await this.post(`${this._baseUrl}/${id}/orphan`, {});
    }

    public async addComment(eventId: string, text: string): Promise<EventCommentEntity> {
        const response = await this.post(`${this._baseUrl}/${eventId}/comments`, { text });
        return EventCommentEntity.fromDto(response.content);
    }

    public async updateComment(eventId: string, commentId: string, text: string): Promise<EventCommentEntity> {
        const response = await this.patch(`${this._baseUrl}/${eventId}/comments/${commentId}`, { text });
        return EventCommentEntity.fromDto(response.content);
    }

    public async listComments(eventId: string): Promise<EventCommentEntity[]> {
        const response = await this.get(`${this._baseUrl}/${eventId}/comments`);
        return EventCommentEntity.fromDtoArray(response.content);
    }

    public async deleteComment(eventId: string, commentId: string): Promise<void> {
        await this.delete(`${this._baseUrl}/${eventId}/comments/${commentId}`);
    }

    public async claim(code: string, pin?: string): Promise<{ statusCode: number }> {
        const response = await this.post(`${this._baseUrl}/${code}/claim`, { pin: pin ?? null });
        return { statusCode: response.statusCode };
    }

    public async submitRegistration(code: string, payload: any): Promise<{ statusCode: number }> {
        const response = await this.post(`${this._baseUrl}/${encodeURIComponent(code)}/register`, payload);
        return { statusCode: response.statusCode };
    }

    public async copy(code: string): Promise<EventEntity> {
        const response = await this.post(`${this._baseUrl}/${encodeURIComponent(code)}/copy`, {});
        return EventEntity.fromDto(response.content);
    }
}
